import React from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Lock, Sparkles, MessageCircle, ArrowRight, CheckCircle2, ShieldAlert } from 'lucide-react';
import { useBusinessStore } from '../store/useBusinessStore';
import { MODULE_DEFINITIONS, isModuleEnabled } from '../utils/businessConfigHelpers';

// hidden: módulo aún no ofrecido a todos; si no está encendido no se muestra la pantalla de venta, se vuelve al inicio.
export default function FeatureGate({ moduleKey, hidden = false, children }) {
  const { masterDb, activeBusinessId } = useBusinessStore();
  const navigate = useNavigate();
  
  const activeBusiness = masterDb.find(b => String(b.id) === String(activeBusinessId)) || masterDb[0] || null;
  const isEnabled = isModuleEnabled(activeBusiness, moduleKey);

  // If enabled or no specific module key given, render child component normally
  if (isEnabled || !moduleKey) {
    return children;
  }

  if (hidden) return <Navigate to="/v2/dashboard" replace />;

  const moduleInfo = MODULE_DEFINITIONS[moduleKey] || {
    name: 'Módulo Premium',
    category: 'Funcionalidad Avanzada',
    description: 'Esta característica requiere una actualización de plan o activación directa por el administrador.',
    benefits: [
      'Acceso completo a herramientas avanzadas para tu negocio',
      'Soporte técnico prioritario y configuración guiada',
      'Mayor automatización de tus procesos operativos'
    ]
  };

  const businessName = activeBusiness?.nombre_negocio || 'Mi Negocio';
  const whatsappMsg = encodeURIComponent(
    `Hola Keyla CRM / Seryiads, me gustaría activar el módulo de *${moduleInfo.name}* para mi negocio *${businessName}* (ID: ${activeBusiness?.id || 'N/A'}). ¿Cómo podemos habilitarlo?`
  );
  const whatsappUrl = `https://wa.me/573009292835?text=${whatsappMsg}`;

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-seryi-bg/90 border border-seryi-bg-light/80 rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden backdrop-blur-xl">
        {/* Glow Effects */}
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-seryi-green/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="space-y-6 relative z-10">
          {/* Header Badge & Title */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-seryi-bg-light/80 pb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shadow-lg shadow-amber-500/10 shrink-0">
                <Lock size={26} className="animate-pulse" />
              </div>
              <div>
                <span className="text-[10px] uppercase font-mono font-black text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2.5 py-0.5 rounded-full inline-block mb-1">
                  Módulo Bloqueado en Plan Actual
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-seryi-heading">
                  {moduleInfo.name}
                </h2>
              </div>
            </div>
            <span className="text-xs text-seryi-text font-medium bg-seryi-bg-dark px-3 py-1.5 rounded-xl border border-seryi-bg-light/50 self-start sm:self-center">
              {moduleInfo.category}
            </span>
          </div>

          {/* Explanation */}
          <div className="space-y-2">
            <p className="text-xs sm:text-sm text-seryi-text leading-relaxed">
              {moduleInfo.description}
            </p>
            <p className="text-xs text-amber-400/90 font-medium">
              Actualmente tu cuenta (<span className="text-seryi-heading font-bold">{businessName}</span>) no tiene habilitada esta función en su configuración.
            </p>
          </div>

          {/* Benefits Box */}
          <div className="bg-seryi-bg-dark/80 border border-seryi-bg-light rounded-2xl p-5 space-y-3">
            <h4 className="text-xs font-bold text-seryi-heading flex items-center gap-2">
              <Sparkles size={14} className="text-seryi-green" />
              ¿Qué obtienes al activar este módulo?
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {moduleInfo.benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs text-slate-300">
                  <CheckCircle2 size={15} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Call to Actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black py-3.5 px-6 rounded-2xl text-xs sm:text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
            >
              <MessageCircle size={16} />
              <span>Activar con Soporte / WhatsApp</span>
            </a>

            <Link
              to="/planes"
              className="bg-seryi-bg-light hover:bg-slate-700 text-seryi-heading font-bold py-3.5 px-5 rounded-2xl text-xs flex items-center justify-center gap-2 transition-all"
            >
              <span>Ver Planes</span>
              <ArrowRight size={14} />
            </Link>

            <button
              onClick={() => navigate('/v2/dashboard')}
              className="text-seryi-text hover:text-seryi-heading text-xs py-3 px-4 rounded-2xl transition-colors font-medium text-center"
            >
              Ir al Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
